// Trigger sets — authored per report; see docs/builder-report-authoring-spec.md.
// Batch: liftingOps (crane lifting, telehandler, 360 excavator).
import type { TriggerQuestion } from "./types";
import { OVERHEAD, UNDERGROUND_SERVICES, PLANT_PEDESTRIAN } from "./shared";

// Local hazard-id constants for the conditional (switchable) hazards in this
// batch. Always-on core hazards (overturn, accessory failure, slew strike,
// comms, quick-hitch) are never toggled so they need no constant here.
const CRANE_WIND = "haz-lifting-operations-loss-of-control-in-high-wind";
const TELE_SUSPENDED = "haz-lifting-operations-telehandler-dropped-suspended-load";
const EXC_COLLAPSE = "haz-excavation-excavation-or-trench-collapse";
const EXC_LIFT_DROP = "haz-lifting-operations-excavator-dropped-load-lifting";
const EXC_VIBRATION = "haz-noise-vibration-whole-body-vibration-from-plant-operation";

export const LIFTING_OPS_TRIGGERS: Record<string, TriggerQuestion[]> = {
  "crane-lifting-operations": [
    {
      key: "liftCategory",
      label: "How is the lift categorised in the lift plan?",
      kind: "choice",
      options: ["Basic lift", "Intermediate lift", "Complex lift — out of scope"],
      effects: {
        "Basic lift": { scopeLine: "Lift category: basic — routine lift within a standard plan, controlled by a competent operator and slinger/signaller." },
        "Intermediate lift": { scopeLine: "Lift category: intermediate — a crane supervisor with authority to stop the lift is on site to control it." },
        "Complex lift — out of scope": { failCheck: "Complex lift confirmed — a dedicated written lift plan is required and this template does not cover tandem or complex lifts" },
      },
    },
    {
      key: "liftPlanRef",
      label: "Lift plan reference and appointed person (name)",
      kind: "text",
      placeholder: "e.g. LP-2026-014, A. Singh (appointed person)",
      requiredCheck: "Lift plan reference and appointed person recorded",
      effects: { "*": { scopeLine: "Lift plan {answer} signed off by the appointed person before lifting begins." } },
    },
    {
      key: "thoroughExam",
      label: "Thorough-examination dates for the crane and lifting accessories",
      kind: "text",
      placeholder: "e.g. crane TE 03/2026 (12-monthly), slings/shackles TE 04/2026 (6-monthly)",
      requiredCheck: "LOLER thorough-examination dates recorded for crane and accessories",
      effects: { "*": { scopeLine: "LOLER thorough examination: {answer}. Reports available on site." } },
    },
    {
      key: "operatorCard",
      label: "Crane operator card type and number",
      kind: "text",
      placeholder: "e.g. CPCS A66 mobile crane, card 1234567",
      requiredCheck: "Crane operator card type/number recorded",
      effects: { "*": { scopeLine: "Crane operator: {answer}." } },
    },
    {
      key: "commsMethod",
      label: "What is the agreed communication method for the lift?",
      kind: "choice",
      options: ["Hand signals (BS 7121)", "Two-way radio", "Radio with hand-signal backup"],
      effects: {
        "Hand signals (BS 7121)": { scopeLine: "Communication: hand signals to BS 7121 with clear line of sight; one nominated signaller controls the lift." },
        "Two-way radio": { scopeLine: "Communication: dedicated two-way radio on a clear channel; the operator holds the load and stops if contact is lost." },
        "Radio with hand-signal backup": { scopeLine: "Communication: two-way radio with hand signals to BS 7121 as backup; the operator stops if both are lost." },
      },
    },
    {
      key: "overhead",
      label: "Are overhead power lines near the crane position or slew arc?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OVERHEAD] },
        no: { hazardsOff: [OVERHEAD], emergencyOff: ["overhead power line"], scopeLine: "No overhead power lines within risk distance of the crane position, slew arc or load path." },
      },
      notSure: { hazardsOn: [OVERHEAD], failCheck: "Overhead power line proximity confirmed" },
    },
    {
      key: "windSensitive",
      label: "Is the load wind-sensitive (large sail area) or the position exposed?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [CRANE_WIND] },
        no: { hazardsOff: [CRANE_WIND], emergencyOff: ["high wind"], scopeLine: "Load has a small sail area in a sheltered position — the standard crane wind limit applies and is monitored, but a derated wind-sensitive limit is not required." },
      },
      notSure: { hazardsOn: [CRANE_WIND], failCheck: "Wind-sensitivity of the load confirmed" },
    },
  ],

  telehandler: [
    {
      key: "operatorCard",
      label: "Telehandler operator card type and number",
      kind: "text",
      placeholder: "e.g. CPCS A17 telehandler, card 1234567",
      requiredCheck: "Telehandler operator card type/number recorded",
      effects: { "*": { scopeLine: "Telehandler operator: {answer}; authorised in writing for the task." } },
    },
    {
      key: "authorisation",
      label: "Is the named operator authorised to operate by the site manager?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "The named operator is authorised in writing by the site manager for this machine and task." },
        no: { failCheck: "Written authorisation to operate confirmed for the named operator" },
      },
      notSure: { failCheck: "Written authorisation to operate confirmed for the named operator" },
    },
    {
      key: "attachment",
      label: "What attachment is fitted to the telehandler?",
      kind: "choice",
      options: ["Pallet forks", "Bucket", "Lifting jib / hook (suspended loads)"],
      effects: {
        "Pallet forks": { scopeLine: "Attachment: pallet forks for palletised loads; no freely suspended lifting." },
        Bucket: { scopeLine: "Attachment: rated bucket for loose materials; no freely suspended lifting." },
        "Lifting jib / hook (suspended loads)": { scopeLine: "Attachment: certified lifting jib/hook — freely suspended lifting under LOLER applies." },
      },
    },
    {
      key: "suspendedLoads",
      label: "Will the telehandler lift freely suspended loads (jib/hook)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [TELE_SUSPENDED], permitsAdd: ["Lift plan where the telehandler is used to lift freely suspended loads"], scopeLine: "Freely suspended loads are lifted with a certified LOLER-examined jib/hook by an operator holding the suspended-loads category, with a trained slinger and a lift plan." },
        no: {
          hazardsOff: [TELE_SUSPENDED],
          permitsRemove: ["Lift plan where the telehandler is used to lift freely suspended loads"],
          competenceOff: ["Suspended-loads endorsement"],
          sequenceOff: [6],
          emergencyOff: ["Dropped or shed load"],
          scopeLine: "No freely suspended lifting — loads are carried on forks or in a cage only; hanging loads off the forks with a chain is not permitted.",
        },
      },
      notSure: { hazardsOn: [TELE_SUSPENDED], permitsAdd: ["Lift plan where the telehandler is used to lift freely suspended loads"], failCheck: "Suspended-load requirement confirmed" },
    },
    {
      key: "manBasket",
      label: "Is a man-basket / work platform proposed on the telehandler?",
      kind: "yesno",
      effects: {
        no: { scopeLine: "No man-basket or work platform on the telehandler — lifting people requires a separately certified integrated work platform and is outside this RAMS." },
      },
      notSure: { failCheck: "Man-basket / work platform use confirmed — if proposed it is out of scope and needs a separate RAMS and certified platform" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "manBasketStop",
            label: "Confirm a separate RAMS and certified integrated work platform are in place for lifting people",
            kind: "yesno",
            effects: {
              yes: { scopeLine: "Lifting people is covered by a separate RAMS and a certified integrated work platform — it is not authorised under this telehandler RAMS." },
              no: { failCheck: "Lifting people on a telehandler is out of scope here — a separate RAMS and certified platform are required before any such use" },
            },
            notSure: { failCheck: "Lifting people on a telehandler is out of scope here — a separate RAMS and certified platform are required before any such use" },
          },
        ],
      },
    },
    {
      key: "pedestrians",
      label: "Do pedestrians, other trades or the public share the working area?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [PLANT_PEDESTRIAN], requireFields: ["nearbyTradesOrPublic"] },
        no: { hazardsOff: [PLANT_PEDESTRIAN], emergencyOff: ["Pedestrian struck"], scopeLine: "The telehandler working area is fully segregated with no pedestrians, other trades or public present during operation." },
      },
      notSure: { hazardsOn: [PLANT_PEDESTRIAN], failCheck: "Pedestrian / public interface confirmed" },
    },
  ],

  "excavator-operation": [
    {
      key: "operatorCard",
      label: "Excavator operator card type and number",
      kind: "text",
      placeholder: "e.g. CPCS A59 360 excavator above 10t, card 1234567",
      requiredCheck: "Excavator operator card type/number recorded",
      effects: { "*": { scopeLine: "Excavator operator: {answer}, trained on the quick-hitch type fitted." } },
    },
    {
      key: "quickHitch",
      label: "Quick-hitch type fitted and locking-device check",
      kind: "text",
      placeholder: "e.g. fully automatic hitch; locking device confirmed after every change, coupler checked daily",
      requiredCheck: "Quick-hitch type and locking-device check regime recorded",
      effects: { "*": { scopeLine: "Quick-hitch: {answer}." } },
    },
    {
      key: "services",
      label: "Are underground services present, or is their location unknown?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [UNDERGROUND_SERVICES] },
        no: { hazardsOff: [UNDERGROUND_SERVICES], emergencyOff: ["Underground service strike"], scopeLine: "No underground services in the dig area — confirmed against utility drawings and a CAT/Genny scan before excavation." },
      },
      notSure: { hazardsOn: [UNDERGROUND_SERVICES], failCheck: "Underground service status confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "permitToDig",
            label: "Is a permit to dig in place with CAT/Genny scan and trial holes?",
            kind: "yesno",
            effects: {
              yes: { scopeLine: "Permit to dig in place; services located by CAT/Genny scan and confirmed by hand-dug trial holes before machine digging." },
              no: { failCheck: "Permit to dig, CAT/Genny scan and trial holes confirmed" },
            },
            notSure: { failCheck: "Permit to dig, CAT/Genny scan and trial holes confirmed" },
          },
        ],
      },
    },
    {
      key: "overhead",
      label: "Are overhead power lines near the machine or its reach?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OVERHEAD] },
        no: { hazardsOff: [OVERHEAD], emergencyOff: ["Overhead line contact"], scopeLine: "No overhead power lines within the reach or travel path of the excavator." },
      },
      notSure: { hazardsOn: [OVERHEAD], failCheck: "Overhead power line proximity confirmed" },
    },
    {
      key: "lifting",
      label: "Will the excavator be used to lift pipes or other loads (as a crane)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [EXC_LIFT_DROP], permitsAdd: ["Lift plan where the excavator is used as a crane for pipes or other loads"], scopeLine: "The excavator lifts loads as a planned LOLER operation from a certified lifting point within its lifting chart, with a rated capacity indicator, check valves and a trained slinger." },
        no: {
          hazardsOff: [EXC_LIFT_DROP],
          permitsRemove: ["Lift plan where the excavator is used as a crane for pipes or other loads"],
          sequenceOff: [6],
          scopeLine: "The excavator is used for digging and grading only — no object handling or lifting; chaining a load to the bucket teeth is not permitted.",
        },
      },
      notSure: { hazardsOn: [EXC_LIFT_DROP], permitsAdd: ["Lift plan where the excavator is used as a crane for pipes or other loads"], failCheck: "Excavator lifting requirement confirmed" },
    },
    {
      key: "support",
      label: "How will any excavation alongside the machine be supported?",
      kind: "choice",
      options: ["Battering / benching", "Trench box / shoring", "No deep excavation — surface works only"],
      effects: {
        "Battering / benching": { scopeLine: "Excavation support: sides battered or benched to the assessed angle of repose." },
        "Trench box / shoring": { scopeLine: "Excavation support: trench box / proprietary shoring installed before any entry." },
        "No deep excavation — surface works only": { hazardsOff: [EXC_COLLAPSE], emergencyOff: ["Excavation collapse"], scopeLine: "Surface grading and works only — no excavation deep enough to present a collapse risk; collapse controls are not required for this task." },
      },
    },
    {
      key: "vibration",
      label: "Will the operator have long daily seat time (whole-body vibration)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [EXC_VIBRATION] },
        no: { hazardsOff: [EXC_VIBRATION], scopeLine: "Short or intermittent machine use — daily vibration exposure is well below the action value, so vibration health surveillance is not required for this task." },
      },
      notSure: { hazardsOn: [EXC_VIBRATION], failCheck: "Daily vibration exposure confirmed" },
    },
  ],
};
